import { useEffect, useState } from "react";
import { Button } from "./ui/Button";
import { Icon } from "../app/Icon";
import { startCheckout, getBillingStatus } from "../lib/billing";

/** Gate a posting surface: shows the paywall only when enforcement is live
 *  (server-driven — turns on automatically once the 1,000-member cap is hit)
 *  AND the account can't post free (not a founding member, no active Pro). */
export function usePaywallGate(): [boolean, () => void] {
  const [show, setShow] = useState(false);
  useEffect(() => {
    getBillingStatus()
      .then((s) => { if (s.enforced && !s.can_post_free) setShow(true); })
      .catch(() => { /* fail open — never block posting on a billing error */ });
  }, []);
  return [show, () => setShow(false)];
}

/** Upgrade prompt shown when posting is gated (VITE_BILLING_ENFORCED). */
export function Paywall({ onClose }: { onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  async function upgrade() {
    setBusy(true);
    try {
      const { url } = await startCheckout();
      window.location.href = url;
    } catch {
      setBusy(false);
    }
  }
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-label="Upgrade to Pro">
      <div className="relative w-full max-w-md rounded-card border border-line bg-surface p-8 text-center">
        <button type="button" onClick={onClose} aria-label="Close" className="absolute right-4 top-4 text-muted hover:text-ink">✕</button>
        <span className="mx-auto grid h-16 w-16 place-items-center rounded-full border-2 border-accent/40 text-accent-strong"><Icon name="security" className="h-8 w-8" /></span>
        <h2 className="mt-4 font-display text-2xl font-black tracking-tight">Posting is a Pro feature</h2>
        <p className="mx-auto mt-1 max-w-xs text-sm text-muted">Upgrade your membership to post jobs and shipments and reach more qualified, verified drivers and carriers.</p>
        <div className="mt-6 grid gap-2">
          <Button onClick={upgrade} disabled={busy}>{busy ? "Redirecting…" : "Upgrade to Pro"}</Button>
          <button type="button" onClick={onClose} className="rounded-lg border border-line px-4 py-2 text-sm font-bold text-muted hover:text-ink">Not now</button>
        </div>
      </div>
    </div>
  );
}
